import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
    ArrowLeft,
    RefreshCw,
    Trophy,
    AlertTriangle,
    Frown,
    Snowflake,
    Coins,
    Edit3,
    X,
    Share2,
} from "lucide-react";
import { shareNodeAsImage } from "@/lib/shareImage";
import { RoundsTab } from "@/components/RoundsTab";
import { toast } from "sonner";
import { api, fmtDateTime } from "@/lib/api";

const PHASES = {
    full: { label: "Hele seizoen", testid: "phase-full" },
    pre: { label: "Voor winterstop", testid: "phase-pre" },
    post: { label: "Na winterstop", testid: "phase-post" },
};

export default function SubleagueDetail() {
    const { id } = useParams();
    const subId = parseInt(id, 10);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshState, setRefreshState] = useState({ state: "idle" });
    const [phase, setPhase] = useState("full");
    const [tab, setTab] = useState("standings");
    const [winterEdit, setWinterEdit] = useState(null);
    const pollRef = useRef(null);

    const load = async () => {
        try {
            const r = await api.get(`/subleagues/${subId}`);
            setData(r.data);
        } catch (e) {
            toast.error("Kon subleague niet laden");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        api.get(`/subleagues/${subId}/refresh-status`).then((r) => setRefreshState(r.data));
        return () => clearInterval(pollRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subId]);

    const startRefresh = async () => {
        try {
            const r = await api.post(`/subleagues/${subId}/refresh`);
            setRefreshState(r.data);
            toast.info("Verversen gestart…");
            pollRef.current = setInterval(async () => {
                const s = await api.get(`/subleagues/${subId}/refresh-status`);
                setRefreshState(s.data);
                if (s.data.state === "done" || s.data.state === "error") {
                    clearInterval(pollRef.current);
                    if (s.data.state === "done") {
                        toast.success(s.data.message || "Klaar!");
                        await load();
                    } else {
                        toast.error(s.data.message || "Verversen mislukt");
                    }
                }
            }, 1500);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kon niet starten");
        }
    };

    const saveWinterRound = async () => {
        try {
            await api.patch(`/subleagues/${subId}`, {
                winter_break_round: winterEdit,
            });
            toast.success("Opgeslagen");
            setWinterEdit(null);
            await load();
        } catch (e) {
            toast.error("Opslaan mislukt");
        }
    };

    if (loading) {
        return (
            <div className="space-y-6" data-testid="subleague-loading">
                <div className="shimmer h-12 w-1/2" />
                <div className="shimmer h-64" />
            </div>
        );
    }
    if (!data) return null;

    const coaches = data.coaches || [];
    const sortedByPhase = [...coaches].sort((a, b) => {
        if (phase === "pre") return (b.points_pre_winter || 0) - (a.points_pre_winter || 0);
        if (phase === "post") return (b.points_post_winter || 0) - (a.points_post_winter || 0);
        return (b.total_points || 0) - (a.total_points || 0);
    });

    const empty = (data.max_round || 0) === 0;
    const isRunning = refreshState.state === "running";
    const progressPct = refreshState.total > 0 ? (refreshState.progress / refreshState.total) * 100 : 0;

    return (
        <div className="space-y-8 fade-up" data-testid="subleague-detail-page">
            <Link to="/" className="text-xs uppercase tracking-[0.18em] text-zinc-500 hover:text-[#00FFAA] inline-flex items-center gap-1.5" data-testid="back-link">
                <ArrowLeft size={14} /> Terug
            </Link>

            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-tiny text-[#00FFAA]">Subleague #{data.subleague_id}</div>
                    <h2 className="text-4xl md:text-6xl font-heading mt-2">{data.name}</h2>
                    <div className="text-xs text-zinc-500 uppercase tracking-[0.15em] mt-3 flex flex-wrap gap-x-5 gap-y-1">
                        <span>Coaches: <b className="text-zinc-200">{coaches.length}</b></span>
                        <span>Rondes: <b className="text-zinc-200">{data.max_round || "–"}</b></span>
                        <span>Laatste sync: <b className="text-zinc-200">{fmtDateTime(data.last_refreshed)}</b></span>
                    </div>
                </div>
                <button
                    onClick={startRefresh}
                    disabled={isRunning}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="refresh-btn"
                >
                    <RefreshCw size={16} strokeWidth={3} className={isRunning ? "animate-spin" : ""} />
                    {isRunning ? "Bezig…" : "Ververs data"}
                </button>
            </div>

            {isRunning && (
                <div className="tile p-5" data-testid="refresh-progress">
                    <div className="label-tiny text-[#00FFAA]">{refreshState.message}</div>
                    <div className="mt-3 h-1.5 bg-[#0a0a0a] border border-[#1f1f23] overflow-hidden">
                        <div className="h-full bg-[#00FFAA] transition-all" style={{ width: `${progressPct}%` }} />
                    </div>
                    <div className="mt-2 text-xs text-zinc-500">
                        {refreshState.progress} / {refreshState.total || "?"}
                    </div>
                </div>
            )}

            {empty && !isRunning && (
                <div className="tile p-6 flex items-start gap-4 border-l-4 border-l-[#FFCC00]" data-testid="no-data-warn">
                    <AlertTriangle className="text-[#FFCC00] mt-0.5" size={20} />
                    <div>
                        <div className="label-tiny text-[#FFCC00]">Nog geen data</div>
                        <p className="text-sm text-zinc-300 mt-1">Klik op "Ververs data" om alle rondes op te halen. Dit kan 1–2 minuten duren bij eerste keer.</p>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="flex items-center gap-2 border-b border-[#27272a] flex-wrap" data-testid="detail-tabs">
                {["standings", "rounds", "shame", "pot"].map((t) => (
                    <button
                        key={t}
                        onClick={() => setTab(t)}
                        className={`px-4 py-3 text-xs uppercase tracking-[0.18em] font-bold border-b-2 transition-colors ${
                            tab === t ? "border-[#00FFAA] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"
                        }`}
                        data-testid={`tab-${t}`}
                    >
                        {{ standings: "Klassement", rounds: "Per ronde", shame: "Wall of Shame", pot: "Pot / Boetes" }[t]}
                    </button>
                ))}
            </div>

            {tab === "standings" && (
                <Standings
                    sortedByPhase={sortedByPhase}
                    phase={phase}
                    setPhase={setPhase}
                    winterRound={data.winter_break_round}
                    onEditWinter={() => setWinterEdit(data.winter_break_round)}
                    winterEdit={winterEdit}
                    setWinterEdit={setWinterEdit}
                    saveWinterRound={saveWinterRound}
                />
            )}

            {tab === "rounds" && <RoundsTab data={data} />}

            {tab === "shame" && <WallOfShame coaches={coaches} roundLosers={data.round_losers || []} />}

            {tab === "pot" && <PotTab data={data} reload={load} />}
        </div>
    );
}

function Standings({ sortedByPhase, phase, setPhase, winterRound, onEditWinter, winterEdit, setWinterEdit, saveWinterRound }) {
    const pointsKey = phase === "pre" ? "points_pre_winter" : phase === "post" ? "points_post_winter" : "total_points";
    return (
        <div className="space-y-5" data-testid="standings-section">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="inline-flex border border-[#27272a]" data-testid="phase-toggle">
                    {Object.entries(PHASES).map(([key, p]) => (
                        <button
                            key={key}
                            onClick={() => setPhase(key)}
                            className={`px-4 py-2 text-xs uppercase tracking-[0.16em] font-bold transition-colors ${
                                phase === key ? "bg-[#00FFAA] text-black" : "text-zinc-400 hover:text-white"
                            }`}
                            data-testid={p.testid}
                        >
                            {key === "pre" || key === "post" ? <Snowflake size={11} className="inline mr-1.5" /> : null}
                            {p.label}
                        </button>
                    ))}
                </div>
                <div className="text-xs text-zinc-500 uppercase tracking-[0.15em] flex items-center gap-2">
                    Winterstop:
                    {winterEdit !== null ? (
                        <>
                            <input
                                type="number"
                                className="input-dark py-1 px-2 w-20 text-xs"
                                value={winterEdit}
                                onChange={(e) => setWinterEdit(parseInt(e.target.value, 10) || 1)}
                                data-testid="winter-edit-input"
                            />
                            <button onClick={saveWinterRound} className="btn-ghost py-1 px-2 text-xs" data-testid="winter-save-btn">Opslaan</button>
                            <button onClick={() => setWinterEdit(null)} className="text-zinc-500 hover:text-white">×</button>
                        </>
                    ) : (
                        <button
                            onClick={onEditWinter}
                            className="text-zinc-200 hover:text-[#00FFAA] underline-offset-4 hover:underline"
                            data-testid="winter-edit-btn"
                        >
                            Ronde {winterRound}
                        </button>
                    )}
                </div>
            </div>

            <div className="tile overflow-x-auto" data-testid="standings-table">
                <table className="tac">
                    <thead>
                        <tr>
                            <th className="w-16">Rank</th>
                            <th>Coach</th>
                            <th>Team</th>
                            <th className="text-right">Punten</th>
                            <th className="text-right hidden md:table-cell">Rondes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedByPhase.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="text-center py-12 text-zinc-500">
                                    Geen coaches om te tonen
                                </td>
                            </tr>
                        ) : (
                            sortedByPhase.map((c, i) => (
                                <tr key={c.team_id} data-testid={`standings-row-${c.team_id}`}>
                                    <td>
                                        <span className={`rank-badge ${i === 0 ? "gold" : i <= 2 ? "silver" : ""}`}>{i + 1}</span>
                                    </td>
                                    <td className="font-medium">{c.first_name}</td>
                                    <td className="text-zinc-400">{c.team_name}</td>
                                    <td className="text-right font-heading text-lg">{c[pointsKey] || 0}</td>
                                    <td className="text-right hidden md:table-cell text-zinc-500 text-xs">{c.rounds_played}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function WallOfShame({ coaches, roundLosers }) {
    const [expanded, setExpanded] = useState({}); // { "yellow_cards-121381": true, ... }
    const [showRoundTable, setShowRoundTable] = useState(false);
    const categories = [
        { key: "yellow_cards", events: "yellow_events", label: "Gele kaarten", color: "#FFCC00" },
        { key: "red_cards", events: "red_events", label: "Rode kaarten", color: "#FF3B30" },
        { key: "own_goals", events: "own_goal_events", label: "Eigen doelpunten", color: "#8A2BE2" },
        { key: "low_point_rounds", events: "low_point_events", label: "Rondes < 15 punten", color: "#007AFF" },
    ];

    const toggle = (catKey, teamId) => {
        const k = `${catKey}-${teamId}`;
        setExpanded((prev) => ({ ...prev, [k]: !prev[k] }));
    };
    const isExp = (catKey, teamId) => !!expanded[`${catKey}-${teamId}`];

    if (coaches.length === 0) {
        return <div className="tile p-12 text-center text-zinc-500" data-testid="shame-empty">Geen data beschikbaar.</div>;
    }

    // Coaches sorted by last_place_rounds_count desc (ties stay together)
    const lastPlaceSorted = [...coaches].sort(
        (a, b) => (b.last_place_rounds_count || 0) - (a.last_place_rounds_count || 0)
    );
    const maxLast = lastPlaceSorted[0]?.last_place_rounds_count || 0;
    const totalRounds = roundLosers.length;

    return (
        <div className="space-y-5" data-testid="wall-of-shame">
            {/* Hero card: Vaakst de laagste in een ronde */}
            <div className="tile p-6 border-l-4 border-l-[#FF3B30]" data-testid="shame-last-place-hero">
                <div className="grid md:grid-cols-[280px_1fr] gap-8">
                    <div>
                        <div className="label-tiny text-[#FF3B30]">Vaakst laatste in een ronde</div>
                        <div className="font-heading text-6xl md:text-7xl mt-2 text-[#FF3B30] leading-none">
                            {maxLast}
                            <span className="text-2xl text-zinc-500 ml-2">/ {totalRounds}</span>
                        </div>
                        <div className="text-sm text-zinc-300 mt-3">
                            <b className="text-white">{lastPlaceSorted[0]?.first_name}</b>
                            <span className="text-zinc-500"> · {lastPlaceSorted[0]?.team_name}</span>
                        </div>
                        <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
                            Per speelronde wordt gekeken wie de laagste score had.
                            Bij gelijkstand telt dat voor iedereen die laatste was.
                        </p>
                    </div>
                    <div>
                        <div className="label-tiny mb-3 text-zinc-500">Aantal rondes laatste per coach</div>
                        <div className="space-y-2.5">
                            {lastPlaceSorted.map((c, i) => {
                                const cnt = c.last_place_rounds_count || 0;
                                const pct = maxLast > 0 ? (cnt / maxLast) * 100 : 0;
                                return (
                                    <div key={c.team_id} className="text-sm" data-testid={`shame-last-row-${c.team_id}`}>
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="truncate flex items-center gap-2">
                                                <span className="text-zinc-600 font-heading text-xs w-5">{i + 1}.</span>
                                                <span className={i === 0 ? "text-white font-semibold" : "text-zinc-300"}>{c.first_name}</span>
                                                <span className="text-zinc-600 text-xs hidden sm:inline">· {c.team_name}</span>
                                            </span>
                                            <span className="font-heading text-base" style={{ color: i === 0 ? "#FF3B30" : "#a1a1aa" }}>
                                                {cnt}× <span className="text-zinc-600 text-xs">/ {totalRounds}</span>
                                            </span>
                                        </div>
                                        <div className="h-1 bg-[#0a0a0a]">
                                            <div className="h-full transition-all" style={{ width: `${pct}%`, background: "#FF3B30" }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Per-round breakdown - collapsible */}
            <div className="tile p-0 overflow-hidden" data-testid="shame-round-table">
                <button
                    onClick={() => setShowRoundTable((v) => !v)}
                    className="w-full px-5 py-4 border-b border-[#27272a] flex items-center justify-between hover:bg-[#1a1a1a] transition-colors text-left"
                    data-testid="round-table-toggle"
                >
                    <div className="label-tiny text-[#FF3B30]">
                        Laatste plek per ronde
                        <span className="ml-2 text-zinc-500 normal-case tracking-normal">({roundLosers.length} rondes)</span>
                    </div>
                    <span className="text-[#FF3B30] text-base">{showRoundTable ? "▾" : "▸"}</span>
                </button>
                {showRoundTable && (
                    <div className="overflow-x-auto" data-testid="round-table-body">
                        <table className="tac">
                            <thead>
                                <tr>
                                    <th className="w-20">Ronde</th>
                                    <th>Loser(s)</th>
                                    <th className="text-right">Punten</th>
                                </tr>
                            </thead>
                            <tbody>
                                {roundLosers.length === 0 ? (
                                    <tr><td colSpan={3} className="text-center py-10 text-zinc-500">Geen ronde-data</td></tr>
                                ) : (
                                    roundLosers.map((rl) => (
                                        <tr key={rl.round} data-testid={`round-loser-${rl.round}`}>
                                            <td className="font-heading text-base">R{rl.round}</td>
                                            <td>
                                                {rl.names.map((n, idx) => (
                                                    <span key={idx}>
                                                        <span className="text-white">{n}</span>
                                                        {idx < rl.names.length - 1 && <span className="text-zinc-600"> + </span>}
                                                    </span>
                                                ))}
                                            </td>
                                            <td className="text-right font-heading text-lg" style={{ color: rl.points < 15 ? "#FF3B30" : "#a1a1aa" }}>
                                                {rl.points}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="grid md:grid-cols-2 gap-5">
                {categories.map((cat) => {
                    const sorted = [...coaches].sort((a, b) => (b[cat.key] || 0) - (a[cat.key] || 0));
                    const max = sorted[0]?.[cat.key] || 0;
                    return (
                        <div key={cat.key} className="tile p-5" data-testid={`shame-${cat.key}`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="label-tiny" style={{ color: cat.color }}>{cat.label}</div>
                                    <div className="font-heading text-3xl mt-1.5" style={{ color: cat.color }}>
                                        {sorted.reduce((s, c) => s + (c[cat.key] || 0), 0)}
                                    </div>
                                    <div className="text-xs text-zinc-500 uppercase tracking-[0.15em]">
                                        Totaal in subleague
                                        <span className="ml-2 normal-case tracking-normal" style={{ color: cat.color }}>· klik op naam voor details</span>
                                    </div>
                                </div>
                                <div className="w-12 h-12 flex items-center justify-center text-2xl border border-[#27272a]" style={{ background: `${cat.color}15` }}>
                                    {cat.key === "yellow_cards" && <CardIcon color="#FFCC00" />}
                                    {cat.key === "red_cards" && <CardIcon color="#FF3B30" />}
                                    {cat.key === "own_goals" && <Frown size={22} style={{ color: cat.color }} />}
                                    {cat.key === "low_point_rounds" && <Trophy size={22} style={{ color: cat.color, transform: "rotate(180deg)" }} />}
                                </div>
                            </div>
                            <div className="mt-5 space-y-2">
                                {sorted.slice(0, 5).map((c, i) => {
                                    const pct = max > 0 ? ((c[cat.key] || 0) / max) * 100 : 0;
                                    const events = c[cat.events] || [];
                                    const hasCount = (c[cat.key] || 0) > 0;
                                    const hasEvents = hasCount && events.length > 0;
                                    const open = isExp(cat.key, c.team_id);
                                    return (
                                        <div key={c.team_id} className="text-sm" data-testid={`shame-${cat.key}-row-${c.team_id}`}>
                                            <button
                                                type="button"
                                                onClick={hasEvents ? () => toggle(cat.key, c.team_id) : undefined}
                                                disabled={!hasEvents}
                                                className={`w-full text-left ${hasEvents ? "cursor-pointer hover:opacity-90 transition-opacity" : "cursor-default"}`}
                                                data-testid={hasEvents ? `${cat.key}-toggle-${c.team_id}` : undefined}
                                            >
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="truncate flex items-center gap-2">
                                                        <span className="text-zinc-600 font-heading text-xs w-5">{i + 1}.</span>
                                                        <span className={i === 0 ? "text-white font-semibold" : "text-zinc-300"}>{c.first_name}</span>
                                                        <span className="text-zinc-600 text-xs hidden sm:inline">· {c.team_name}</span>
                                                        {hasEvents && (
                                                            <span className="text-xs" style={{ color: cat.color }}>{open ? "▾" : "▸"}</span>
                                                        )}
                                                    </span>
                                                    <span className="font-heading text-base" style={{ color: i === 0 ? cat.color : "#a1a1aa" }}>
                                                        {c[cat.key] || 0}
                                                    </span>
                                                </div>
                                                <div className="h-1 bg-[#0a0a0a]">
                                                    <div className="h-full transition-all" style={{ width: `${pct}%`, background: cat.color }} />
                                                </div>
                                            </button>
                                            {open && hasEvents && (
                                                <div
                                                    className="mt-2 mb-3 ml-7 border-l-2 pl-3 space-y-1.5"
                                                    style={{ borderColor: cat.color }}
                                                    data-testid={`${cat.key}-events-${c.team_id}`}
                                                >
                                                    {events.map((ev, idx) => (
                                                        <div key={idx} className="text-xs flex items-center gap-2.5">
                                                            <span className="font-heading text-sm" style={{ color: cat.color }}>R{ev.round}</span>
                                                            <span className="text-zinc-300">
                                                                {cat.key === "own_goals" && (ev.players || []).join(", ")}
                                                                {cat.key === "yellow_cards" && `${ev.count}× geel`}
                                                                {cat.key === "red_cards" && `${ev.count}× rood`}
                                                                {cat.key === "low_point_rounds" && `${ev.points} punten`}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function CardIcon({ color }) {
    return <div className="w-4 h-5" style={{ background: color, borderRadius: 1 }} />;
}


function PotTab({ data, reload }) {
    const [editOpen, setEditOpen] = useState(false);
    const [view, setView] = useState("cost"); // "cost" | "count"
    const [sharing, setSharing] = useState(false);
    const shareRef = useRef(null);
    const fees = data.fees || {};
    const p1Finished = data.p1_finished;
    const seasonFinished = data.season_finished;
    const coaches = [...(data.coaches || [])].sort(
        (a, b) => (b.fines?.total || 0) - (a.fines?.total || 0)
    );
    const fmtEuro = (v) => {
        const n = Number(v) || 0;
        const sign = n < 0 ? "−" : "";
        return `${sign}€ ${Math.abs(n).toFixed(2).replace(/\.00$/, "")}`;
    };
    const PendingCell = ({ label }) => (
        <span className="text-zinc-700 text-xs uppercase tracking-[0.15em]" title={`${label} wordt pas toegepast als deze fase is afgerond`}>—</span>
    );
    // Cell renderer: switches between euro amount and raw count.
    const cell = (coach, opts) => {
        if (view === "cost") return fmtEuro(opts.cost);
        return opts.count ?? "—";
    };

    const tableProps = { coaches, fees, p1Finished, seasonFinished, cell, fmtEuro, PendingCell };
    const sharePot = async () => {
        setSharing(true);
        await shareNodeAsImage(shareRef.current, {
            filename: `pot-${data.subleague_id}.png`,
            title: `Pot ${data.name}`,
            text: `Pot-stand ${data.name}: ${fmtEuro(data.pot_total)}`,
        });
        setSharing(false);
    };

    return (
        <div className="space-y-5" data-testid="pot-tab">
            <div className="grid md:grid-cols-[1fr_auto] gap-5 items-end">
                <div className="tile p-6 border-l-4 border-l-[#00FFAA]">
                    <div className="flex items-center gap-3 mb-1">
                        <Coins className="text-[#00FFAA]" size={18} />
                        <div className="label-tiny text-[#00FFAA]">Totale pot</div>
                    </div>
                    <div className="font-heading text-6xl md:text-7xl text-[#00FFAA] leading-none mt-2">
                        {fmtEuro(data.pot_total)}
                    </div>
                    <p className="text-sm text-zinc-400 mt-3 max-w-2xl">
                        Som van alle individuele boetes. Periode-stand boetes worden pas
                        toegepast als die fase is afgerond.
                    </p>
                    <div className="flex flex-wrap gap-3 mt-4 text-xs uppercase tracking-[0.15em]">
                        <span className={`stat-pill ${p1Finished ? "" : "opacity-50"}`} style={{ background: p1Finished ? "#00FFAA22" : "#0a0a0a", color: p1Finished ? "#00FFAA" : "#71717a", border: "1px solid #27272a" }} data-testid="badge-p1">
                            P1 {p1Finished ? "afgerond" : "nog bezig"}
                        </span>
                        <span className={`stat-pill ${seasonFinished ? "" : "opacity-50"}`} style={{ background: seasonFinished ? "#00FFAA22" : "#0a0a0a", color: seasonFinished ? "#00FFAA" : "#71717a", border: "1px solid #27272a" }} data-testid="badge-season">
                            Seizoen {seasonFinished ? "afgerond" : "nog bezig"}
                        </span>
                    </div>
                </div>
                <div className="flex flex-col gap-2 items-stretch md:items-end">
                    <div className="inline-flex border" style={{ borderColor: "var(--border)" }} data-testid="view-toggle">
                        {[
                            { key: "cost", label: "€ Kosten", testid: "view-cost" },
                            { key: "count", label: "# Aantal", testid: "view-count" },
                        ].map((o) => (
                            <button
                                key={o.key}
                                onClick={() => setView(o.key)}
                                className="px-4 py-2 text-xs uppercase tracking-[0.16em] font-bold transition-colors"
                                style={
                                    view === o.key
                                        ? { background: "var(--accent)", color: "var(--accent-text)" }
                                        : { color: "var(--text-muted)" }
                                }
                                data-testid={o.testid}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => setEditOpen(true)}
                        className="btn-ghost inline-flex items-center gap-2"
                        data-testid="edit-rules-btn"
                    >
                        <Edit3 size={14} /> Regels bewerken
                    </button>
                    <button
                        onClick={sharePot}
                        disabled={sharing}
                        className="btn-ghost inline-flex items-center gap-2 disabled:opacity-50"
                        data-testid="share-pot-btn"
                    >
                        <Share2 size={14} /> {sharing ? "Bezig…" : "Delen als afbeelding"}
                    </button>
                </div>
            </div>

            {/* Coach breakdown */}
            <div className="tile p-0 overflow-hidden" data-testid="pot-table">
                <div className="overflow-x-auto">
                    <PotTable {...tableProps} rowPrefix="pot-row" />
                </div>
            </div>

            <div aria-hidden="true" style={{ position: "fixed", left: -10000, top: 0 }}>
            <div ref={shareRef} style={{ width: 1100, padding: 32, background: "var(--bg)", color: "var(--text)" }} data-testid="pot-share-card">
                <div className="flex items-end justify-between gap-8 mb-5">
                    <div className="min-w-0">
                        <div className="label-tiny text-[#00FFAA]">Pot / Boetes · {view === "cost" ? "Kosten" : "Aantal"}</div>
                        <div className="font-heading text-4xl mt-1">{data.name}</div>
                    </div>
                    <div className="text-right shrink-0">
                        <div className="label-tiny">Totale pot</div>
                        <div className="font-heading text-5xl text-[#00FFAA] leading-none whitespace-nowrap mt-1">{fmtEuro(data.pot_total)}</div>
                    </div>
                </div>
                <div className="tile p-0">
                    <PotTable {...tableProps} rowPrefix="share-row" />
                </div>
                <div className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>Stand van {new Date().toLocaleDateString("nl-NL")}</div>
            </div>
            </div>

            {editOpen && (
                <RulesEditor
                    fees={fees}
                    coachCount={(data.coaches || []).length}
                    subleagueId={data.subleague_id}
                    winterRound={data.winter_break_round}
                    seasonFinished={seasonFinished}
                    onClose={() => setEditOpen(false)}
                    onSaved={async () => {
                        setEditOpen(false);
                        await reload();
                    }}
                />
            )}
        </div>
    );
}

function PotTable({ coaches, fees, p1Finished, seasonFinished, cell, fmtEuro, PendingCell, rowPrefix }) {
    return (
        <table className="tac">
            <thead>
                <tr>
                    <th className="w-10">#</th>
                    <th>Coach</th>
                    <th className="text-right">Geel</th>
                    <th className="text-right">Rood</th>
                    <th className="text-right">OG</th>
                    <th className="text-right">&lt; {fees.low_points_threshold}</th>
                    <th className="text-right">Laatste</th>
                    <th className="text-right">100+</th>
                    <th className="text-right">P1</th>
                    <th className="text-right">P2</th>
                    <th className="text-right">Overall</th>
                    <th className="text-right border-l border-[#27272a]">Totaal</th>
                </tr>
            </thead>
            <tbody>
                {coaches.map((c, i) => (
                    <tr key={c.team_id} data-testid={`${rowPrefix}-${c.team_id}`}>
                        <td><span className="rank-badge">{i + 1}</span></td>
                        <td>
                            <div className="font-medium">{c.first_name}</div>
                            <div className="text-xs text-zinc-500">P1 #{c.period1_rank} · P2 #{c.period2_rank}</div>
                        </td>
                        <td className="text-right text-zinc-400">{cell(c, { cost: c.fines.yellow, count: c.yellow_cards })}</td>
                        <td className="text-right text-zinc-400">{cell(c, { cost: c.fines.red, count: c.red_cards })}</td>
                        <td className="text-right text-zinc-400">{cell(c, { cost: c.fines.own_goal, count: c.own_goals })}</td>
                        <td className="text-right text-zinc-400">{cell(c, { cost: c.fines.low_points, count: c.low_point_rounds })}</td>
                        <td className="text-right text-zinc-400">{cell(c, { cost: c.fines.last_place, count: c.last_place_rounds_count })}</td>
                        <td className="text-right" style={{ color: c.fines.high_points_bonus < 0 ? "#00FFAA" : "#71717a" }}>
                            {cell(c, { cost: c.fines.high_points_bonus, count: c.high_point_rounds })}
                        </td>
                        <td className="text-right text-zinc-400">
                            {p1Finished ? cell(c, { cost: c.fines.period1, count: `#${c.period1_rank}` }) : <PendingCell label="P1" />}
                        </td>
                        <td className="text-right text-zinc-400">
                            {seasonFinished ? cell(c, { cost: c.fines.period2, count: `#${c.period2_rank}` }) : <PendingCell label="P2" />}
                        </td>
                        <td className="text-right" style={{ color: c.fines.overall < 0 ? "#00FFAA" : "#a1a1aa" }}>
                            {seasonFinished ? cell(c, { cost: c.fines.overall, count: `#${c.overall_rank}` }) : <PendingCell label="Overall" />}
                        </td>
                        <td className="text-right font-heading text-lg border-l border-[#27272a]" style={{ color: c.fines.total > 0 ? "#FF3B30" : "#00FFAA" }}>
                            {fmtEuro(c.fines.total)}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function RulesEditor({ fees, subleagueId, winterRound, seasonFinished, coachCount, onClose, onSaved }) {
    const [form, setForm] = useState({
        yellow_card: fees.yellow_card ?? 2,
        red_card: fees.red_card ?? 10,
        own_goal: fees.own_goal ?? 10,
        low_points: fees.low_points ?? 5,
        low_points_threshold: fees.low_points_threshold ?? 15,
        last_place: fees.last_place ?? 2,
        high_points_bonus: fees.high_points_bonus ?? -10,
        high_points_threshold: fees.high_points_threshold ?? 100,
        winter_break_round: winterRound ?? 17,
        season_finished: !!seasonFinished,
        period_rank_fees: fillRanks(fees.period_rank_fees, coachCount),
        overall_rank_fees: fillRanks(fees.overall_rank_fees, coachCount),
    });
    const [saving, setSaving] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: parseFloat(e.target.value) });

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                yellow_card: form.yellow_card,
                red_card: form.red_card,
                own_goal: form.own_goal,
                low_points: form.low_points,
                low_points_threshold: parseInt(form.low_points_threshold, 10),
                last_place: form.last_place,
                high_points_bonus: form.high_points_bonus,
                high_points_threshold: parseInt(form.high_points_threshold, 10),
                winter_break_round: parseInt(form.winter_break_round, 10),
                season_finished: !!form.season_finished,
                period_rank_fees: form.period_rank_fees.map((v) => Number(v) || 0),
                overall_rank_fees: form.overall_rank_fees.map((v) => Number(v) || 0),
            };
            await api.patch(`/subleagues/${subleagueId}`, payload);
            toast.success("Regels opgeslagen");
            await onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Opslaan mislukt");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
            data-testid="rules-modal"
        >
            <div
                className="tile w-full max-w-2xl my-8"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-6 py-4 border-b border-[#27272a] flex items-center justify-between">
                    <div>
                        <div className="label-tiny text-[#00FFAA]">Subleague regels</div>
                        <h3 className="font-heading text-2xl mt-0.5">Tarieven & boetes</h3>
                    </div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="rules-close">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-6 space-y-6">
                    <div>
                        <div className="label-tiny mb-3">Per ronde</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <Field label="Gele kaart" suffix="€" value={form.yellow_card} onChange={set("yellow_card")} testid="fee-yellow" />
                            <Field label="Rode kaart" suffix="€" value={form.red_card} onChange={set("red_card")} testid="fee-red" />
                            <Field label="Eigen doelpunt" suffix="€" value={form.own_goal} onChange={set("own_goal")} testid="fee-og" />
                            <Field label={`< ${form.low_points_threshold} pt boete`} suffix="€" value={form.low_points} onChange={set("low_points")} testid="fee-low" />
                            <Field label="Drempel <" suffix="pt" value={form.low_points_threshold} onChange={set("low_points_threshold")} testid="fee-low-threshold" />
                            <Field label="Laatste plek" suffix="€" value={form.last_place} onChange={set("last_place")} testid="fee-last" />
                            <Field label="100+ pt bonus" suffix="€" value={form.high_points_bonus} onChange={set("high_points_bonus")} testid="fee-bonus" />
                            <Field label="Bonus drempel" suffix="pt" value={form.high_points_threshold} onChange={set("high_points_threshold")} testid="fee-bonus-threshold" />
                            <Field label="Winterstop ronde" suffix="R" value={form.winter_break_round} onChange={set("winter_break_round")} testid="fee-winter" />
                        </div>
                    </div>

                    <RankFeeFields title="Periode-stand boete (per periode)" values={form.period_rank_fees} prefix="p" onChange={(arr) => setForm({ ...form, period_rank_fees: arr })} />

                    <div>
                        <RankFeeFields title="Overall stand correctie (eind seizoen)" values={form.overall_rank_fees} prefix="o" onChange={(arr) => setForm({ ...form, overall_rank_fees: arr })} />
                        <p className="text-xs text-zinc-500 mt-2">Negatief = korting op de boete (bv. nr 1 krijgt −€20).</p>
                    </div>

                    <label className="flex items-center gap-3 cursor-pointer p-3 bg-[#0a0a0a] border border-[#27272a] hover:border-[#00FFAA] transition-colors" data-testid="season-finished-toggle">
                        <input
                            type="checkbox"
                            checked={!!form.season_finished}
                            onChange={(e) => setForm({ ...form, season_finished: e.target.checked })}
                            className="w-4 h-4 accent-[#00FFAA]"
                            data-testid="season-finished-checkbox"
                        />
                        <div className="flex-1">
                            <div className="text-sm font-semibold">Seizoen afgerond</div>
                            <div className="text-xs text-zinc-500 mt-0.5">
                                Pas aanvinken als alle rondes gespeeld zijn. Dan worden P2- en Overall-boetes pas in de pot meegenomen.
                            </div>
                        </div>
                    </label>
                </div>
                <div className="px-6 py-4 border-t border-[#27272a] flex items-center justify-between">
                    <button onClick={onClose} className="btn-ghost text-xs" data-testid="rules-cancel">Annuleren</button>
                    <button onClick={save} disabled={saving} className="btn-primary" data-testid="rules-save">
                        {saving ? "Opslaan…" : "Opslaan"}
                    </button>
                </div>
            </div>
        </div>
    );
}

const fillRanks = (list, n) => Array.from({ length: Math.max(n, (list || []).length) }, (_, i) => (list || [])[i] ?? 0);

function RankFeeFields({ title, values, prefix, onChange }) {
    const update = (i) => (e) => onChange(values.map((v, j) => (j === i ? parseFloat(e.target.value) : v)));
    return (
        <div>
            <div className="label-tiny mb-3">{title}</div>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3" data-testid={`rank-fees-${prefix}`}>
                {values.map((v, i) => (
                    <Field key={i} label={`Nr ${i + 1}`} suffix="€" value={v} onChange={update(i)} testid={`fee-${prefix}${i + 1}`} />
                ))}
            </div>
        </div>
    );
}

function Field({ label, suffix, value, onChange, testid }) {
    return (
        <label className="block">
            <span className="label-tiny text-zinc-500 block mb-1.5">{label}</span>
            <div className="relative">
                <input
                    type="number"
                    step="any"
                    value={Number.isNaN(value) ? "" : value}
                    onChange={onChange}
                    className="input-dark pr-9"
                    data-testid={testid}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500">{suffix}</span>
            </div>
        </label>
    );
}
